
import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { IMember, ITeamWebsite } from "../../../../services";
import { CacheManager, Utils } from "../../../../utils";
import { useComponentStyle } from "../../../../hooks/useComponentStyle";
import { Classname, DateHelper, StringHelper } from "@rivtn/noseur";
import LanguageDropdown from "../../../../shared/LanguageDropdown";
import { i18nManager } from "../../../../i18n";
import Home from "./Home";
import Schedule from "./Schedule";
import Gallery from "./Gallery";
import Members from "./Members";
import News from "./News";
import Activities from "./Activities";

export interface LandingProps {
    publicId: string;
    basePath: string;
    website: ITeamWebsite;
}

function Landing(props: LandingProps) {

    const mainLabels = i18nManager.Labels;
    const today = DateHelper.getStartOfDay();
    const { website, basePath, publicId } = props;
    const ddRef = React.useRef<HTMLDivElement>(null);
    const nameSplit = website.team.name.split(/\s+/);
    const [nameFirstWord, ...nameRemain] = nameSplit;
    const [cart, setCart] = React.useState<any[]>([]);
    const [showDropdown, setShowDropdown] = React.useState<boolean>(false);
    const [searchParams, _] = ReactRouterDom.useSearchParams(window.location.search);
    const [profile, setProfile] = React.useState<IMember | undefined>(CacheManager.get(CacheManager.PROFILE_KEY) as IMember);
    const colors = searchParams.get("ds_colors")?.length ? JSON.parse(searchParams.get("ds_colors") ?? "{}") : (website.design_settings.scheme_settings?.colors ?? {});
    useComponentStyle(`
        :root {
            --red: ${colors.red ?? "#EF4444"};
            --dark: ${colors.dark ?? "#0A0A0F"};
            --text: ${colors.text ?? "#E8E8F0"};
            --gold: ${colors.gold ?? "#C9A84C"};
            --green: ${colors.green ?? "#22C55E"};
            --dark2: ${colors.dark2 ?? "#111118"};
            --dark3: ${colors.dark3 ?? "#1A1A24"};
            --dark4: ${colors.dark4 ?? "#22222E"};
            --muted: ${colors.muted ?? "#7A7A96"};
            --accent: ${colors.accent ?? "#4F6EF7"};
            --orange: ${colors.orange ?? "#F97316"};
            --gold-light: ${colors.gold_light ?? "#E8C96A"};
            --header-h: 72px;
        }
    `);

    React.useEffect(() => {
        Utils.updateMeta({
            title: website.team.name,
            desc: website.team.description,
            logo: website.team.logo_url ?? website.media?.LOGO?.[0]
        });
    }, [website]);

    React.useEffect(() => {
        const h = (e: MouseEvent): void => {
            if (ddRef.current && !ddRef.current.contains(e.target as Node)) setShowDropdown(false);
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    return (
        <>
            <header className={styles["hdr"]}>
                <ReactRouterDom.NavLink to={`/${basePath}`} className={styles["hdr-logo"]} style={{ textDecoration: "none" }}>
                    <img className={styles["hdr-crest"]} src={website.team.logo_url ?? website.media?.LOGO?.[0]} alt={website.team.name} />
                    {nameFirstWord} <span>{nameRemain.join(" ")}</span>
                    <span className={styles["logo-badge"]}>{nameSplit.map((s) => s[0]).join("")}</span>
                </ReactRouterDom.NavLink>

                <nav className={styles["hdr-nav"]}>
                    {Utils.entriesInCustomOrder(website.website_pages, ["HOME", "MERCH", "SCHEDULE", "MEMBERS", "GALLERY", "ACTIVITIES", "ACHIEVEMENTS", "NEWS"]).filter(([_, pageSettings]) => pageSettings.visible).map(([page, pageSettings]) => (
                        <ReactRouterDom.NavLink end key={page} to={{ pathname: `/${basePath}${page === "HOME" ? "" : "/" + page.toLowerCase()}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`nav-btn`], { [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                    ))}
                </nav>

                <div className={styles["hdr-right"]}>
                    {cart.length > 0 && (
                        <span style={{ fontSize: "0.8rem", color: "var(--gold)" }}>🛒 {cart.length}</span>
                    )}
                    <LanguageDropdown />
                    {(website.extra_data.enable_visitor_account_management) && ((!profile) ? (
                        <button className={styles["btn-signin"]} onClick={() => Utils.signIn()}>Sign In</button>
                    ) : (
                        <div className={styles["dropdown-wrap"]} ref={ddRef}>
                            <img className={styles["avatar-btn"]} onClick={() => setShowDropdown((v: boolean) => !v)} src={profile.profile_picture_url} alt={profile.username} />
                            {showDropdown && (
                                <div className={styles["dropdown"]}>
                                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}`} className={({ }) => Classname.build(styles["dd-item"])}>📊 Dashboard</ReactRouterDom.NavLink>
                                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/teams`} className={({ }) => Classname.build(styles["dd-item"])}>⚙️ My Teams</ReactRouterDom.NavLink>
                                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/tournaments?section=REGISTERED`} className={({ }) => Classname.build(styles["dd-item"])}>🏆 My Tournaments</ReactRouterDom.NavLink>
                                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_SUPPORT_URL}`} className={({ }) => Classname.build(styles["dd-item"])}>🎧 Support</ReactRouterDom.NavLink>
                                    <hr className={styles["dd-divider"]} />
                                    <button className={`${styles["dd-item"]} ${styles["logout"]}`} onClick={() => {
                                        CacheManager.clear();
                                        setProfile(undefined);
                                    }}>← Sign Out</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </header>

            <main className={styles["page"]}>
                <ReactRouterDom.Routes>
                    <ReactRouterDom.Route path="/news" element={<News website={website} />} />
                    <ReactRouterDom.Route path="/members" element={<Members website={website} />} />
                    <ReactRouterDom.Route path="/gallery" element={<Gallery website={website} />} />
                    <ReactRouterDom.Route path="/activities" element={<Activities website={website} />} />
                    <ReactRouterDom.Route path="/schedule" element={<Schedule website={website} publicId={publicId} />} />
                    <ReactRouterDom.Route path="/" element={<Home website={website} basePath={basePath} publicId={publicId} />} />
                </ReactRouterDom.Routes>
            </main>

            {/* FOOTER */}
            <footer className={styles["footer"]}>
                <div className={styles["footer-inner"]}>
                    <div>
                        <div className={styles["footer-logo"]}>{website.owns_copyright ? website.team.name : mainLabels.omnehost}</div>
                        <p className={styles["footer-desc"]}>
                            {website.owns_copyright ? website.team.description : mainLabels.omnehost_detail}
                        </p>
                    </div>
                    <div>
                        <div className={styles["footer-col-title"]}>Navigation</div>
                        <ul className={styles["footer-links"]}>
                            {Utils.entriesInCustomOrder(website.website_pages).filter(([_, pageSettings]) => pageSettings.visible).map(([page, pageSettings]) => (<li key={page}>
                                <ReactRouterDom.NavLink end to={{ pathname: `/${basePath}${page === "HOME" ? "" : "/" + page.toLowerCase()}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build({ [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                            </li>))}
                        </ul>
                    </div>
                    <div>
                        <div className={styles["footer-col-title"]}>Connect</div>
                        <ul className={styles["footer-links"]}>
                            {Object.entries(website.social_accounts ?? {}).map(([social, username]) => (<li key={social}>
                                <ReactRouterDom.NavLink target="_blank" to={Utils.getSocialLink(social, username)}>{StringHelper.toSaneSentenceFormat(social)}</ReactRouterDom.NavLink>
                            </li>))}
                        </ul>
                    </div>
                    {(!website.owns_copyright) && (<div>
                        <div className={styles["footer-col-title"]}>{mainLabels.omnehost}</div>
                        <ul className={styles["footer-links"]}>
                            <li>
                                {(!!profile)
                                    ? (<ReactRouterDom.NavLink target="_blank" to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}>{mainLabels.auth.dashboard}</ReactRouterDom.NavLink>)
                                    : (<ReactRouterDom.NavLink target="_blank" to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}>{mainLabels.auth.sign_in}</ReactRouterDom.NavLink>)}
                            </li>
                            {mainLabels.footer.map((f) => <li key={f.label}><ReactRouterDom.NavLink key={f.label} target="_blank" to={f.location}>{f.label}</ReactRouterDom.NavLink></li>)}
                        </ul>
                    </div>)}
                </div>
                <div className={styles["footer-bottom"]}>
                    <span>© {today.getFullYear()} {website.owns_copyright ? website.team.name : mainLabels.omnehost}. {mainLabels.landing.all_r_reserved}</span>
                    <div className={styles["footer-socials"]}>
                        {Object.entries(website.owns_copyright ? (website.social_accounts ?? {}) : Utils.OMNEHOST_SOCIALS).map(([social, username]) => (
                            <ReactRouterDom.NavLink key={social} target="_blank" to={Utils.getSocialLink(social, username)} className={styles["footer-social-btn"]}><i className={Utils.getSocialIcon(social)} /></ReactRouterDom.NavLink>
                        ))}
                    </div>
                </div>
            </footer>
        </>
    );
}

export default Landing;